import { FC, useState, useCallback, useMemo } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { VoxProductCatalogItem, VoxRoiCostConfigData, CostScheduleEntry } from '../../models/voxRoi';
import { validateSchedule } from './voxRoiCalc';
import styles from './VoxRoiCostRail.module.css';

export interface VoxRoiCostRailProps {
  config: VoxRoiCostConfigData;
  catalog: VoxProductCatalogItem[];
  onSave: (config: VoxRoiCostConfigData) => Promise<void>;
  isSaving: boolean;
  saveError: string | null;
}

const toIsoDateString = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const VoxRoiCostRail: FC<VoxRoiCostRailProps> = ({ config, catalog, onSave, isSaving, saveError }) => {
  const [costs, setCosts] = useState<Record<string, CostScheduleEntry[]>>(() => {
    const initial: Record<string, CostScheduleEntry[]> = {};
    for (const item of catalog) {
      initial[item.productKey] = config.costs[item.productKey] ?? [];
    }
    return initial;
  });
  const [beforeSelection, setBeforeSelection] = useState<string[]>([...config.beforeSelection]);
  const [avgOrdersPerLoan, setAvgOrdersPerLoan] = useState(config.avgOrdersPerLoan);
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isDirty = useMemo(() => {
    if (avgOrdersPerLoan !== config.avgOrdersPerLoan) return true;
    if (JSON.stringify([...beforeSelection].sort()) !== JSON.stringify([...config.beforeSelection].sort())) return true;
    return JSON.stringify(costs) !== JSON.stringify(config.costs);
  }, [costs, beforeSelection, avgOrdersPerLoan, config]);

  const handleBeforeToggle = useCallback((productKey: string) => {
    setBeforeSelection((prev) =>
      prev.includes(productKey)
        ? prev.filter((k) => k !== productKey)
        : [...prev, productKey],
    );
  }, []);

  const handleAddEntry = useCallback((productKey: string) => {
    setCosts((prev) => {
      const existing = prev[productKey] ?? [];
      const newEntry: CostScheduleEntry = {
        effectiveFrom: toIsoDateString(new Date()),
        effectiveTo: null,
        cost: 0,
      };
      return { ...prev, [productKey]: [...existing, newEntry] };
    });
  }, []);

  const handleUpdateEntry = useCallback((productKey: string, index: number, updates: Partial<CostScheduleEntry>) => {
    setCosts((prev) => {
      const entries = [...(prev[productKey] ?? [])];
      entries[index] = { ...entries[index], ...updates };
      return { ...prev, [productKey]: entries };
    });
  }, []);

  const handleRemoveEntry = useCallback((productKey: string, index: number) => {
    setCosts((prev) => {
      const entries = (prev[productKey] ?? []).filter((_, i) => i !== index);
      return { ...prev, [productKey]: entries };
    });
  }, []);

  const handleSave = useCallback(async () => {
    for (const [productKey, schedule] of Object.entries(costs)) {
      if (schedule.length > 0) {
        const scheduleError = validateSchedule(schedule);
        if (scheduleError) {
          setValidationError(`${productKey}: ${scheduleError}`);
          return;
        }
      }
    }
    setValidationError(null);
    await onSave({
      version: config.version,
      avgOrdersPerLoan,
      costs,
      beforeSelection,
    });
  }, [onSave, config.version, avgOrdersPerLoan, costs, beforeSelection]);

  const handleCancel = useCallback(() => {
    setCosts(() => {
      const initial: Record<string, CostScheduleEntry[]> = {};
      for (const item of catalog) {
        initial[item.productKey] = config.costs[item.productKey] ?? [];
      }
      return initial;
    });
    setBeforeSelection([...config.beforeSelection]);
    setAvgOrdersPerLoan(config.avgOrdersPerLoan);
    setValidationError(null);
  }, [config, catalog]);

  return (
    <div className={styles.rail}>
      <div className={styles.railHeader}>
        <span className={styles.railTitle}>Cost Configuration</span>
      </div>

      <div className={styles.railSection}>
        <label htmlFor="rail-avg-orders" className={styles.fieldLabel}>Avg orders / loan</label>
        <InputNumber
          inputId="rail-avg-orders"
          value={avgOrdersPerLoan}
          onValueChange={(e) => { setAvgOrdersPerLoan(e.value ?? 1); }}
          minFractionDigits={1}
          maxFractionDigits={2}
          min={0.1}
          max={99}
          showButtons
          step={0.1}
          inputClassName={styles.smallInput}
        />
      </div>

      <div className={styles.railSection}>
        <span className={styles.fieldLabel}>Product Costs</span>
        <div className={styles.productList}>
          {catalog.map((item) => {
            const schedule = costs[item.productKey] ?? [];
            const isExpanded = expandedProduct === item.productKey;
            const isBefore = beforeSelection.includes(item.productKey);
            const latestCost = schedule.length > 0
              ? schedule[schedule.length - 1].cost
              : undefined;

            return (
              <div key={item.productKey} className={styles.productItem}>
                <div className={styles.productRow}>
                  <Checkbox
                    checked={isBefore}
                    onChange={() => handleBeforeToggle(item.productKey)}
                    aria-label={`Before ${item.productDisplayName}`}
                    className={styles.beforeCheck}
                  />
                  <button
                    type="button"
                    className={styles.productToggle}
                    onClick={() => setExpandedProduct(isExpanded ? null : item.productKey)}
                    aria-expanded={isExpanded}
                  >
                    <i className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'} ${styles.chevron}`} />
                    <div className={styles.productInfo}>
                      <span className={styles.productName}>{item.productDisplayName}</span>
                      <span className={styles.productVendor}>{item.vendor}</span>
                    </div>
                  </button>
                  <span className={styles.productCostBadge}>
                    {latestCost !== undefined ? `$${latestCost.toFixed(2)}` : '---'}
                  </span>
                </div>

                {isExpanded && (
                  <div className={styles.schedulePanel}>
                    {schedule.length === 0 && (
                      <div className={styles.noEntries}>No cost entries</div>
                    )}
                    {schedule.map((entry, idx) => (
                      <div key={idx} className={styles.scheduleEntry}>
                        <div className={styles.entryRow}>
                          <div className={styles.entryField}>
                            <label className={styles.entryLabel}>From</label>
                            <Calendar
                              value={new Date(entry.effectiveFrom + 'T00:00:00')}
                              onChange={(e) => {
                                if (e.value) handleUpdateEntry(item.productKey, idx, { effectiveFrom: toIsoDateString(e.value as Date) });
                              }}
                              dateFormat="yy-mm-dd"
                              showIcon
                              className={styles.datePicker}
                            />
                          </div>
                          <div className={styles.entryField}>
                            <label className={styles.entryLabel}>To</label>
                            <Calendar
                              value={entry.effectiveTo ? new Date(entry.effectiveTo + 'T00:00:00') : null}
                              onChange={(e) => {
                                handleUpdateEntry(item.productKey, idx, {
                                  effectiveTo: e.value ? toIsoDateString(e.value as Date) : null,
                                });
                              }}
                              dateFormat="yy-mm-dd"
                              showIcon
                              className={styles.datePicker}
                              placeholder="Open-ended"
                            />
                          </div>
                        </div>
                        <div className={styles.entryRow}>
                          <div className={styles.entryField}>
                            <label className={styles.entryLabel}>Cost ($)</label>
                            <InputNumber
                              value={entry.cost}
                              onValueChange={(e) => handleUpdateEntry(item.productKey, idx, { cost: e.value ?? 0 })}
                              mode="currency"
                              currency="USD"
                              minFractionDigits={2}
                              maxFractionDigits={2}
                              min={0}
                              inputClassName={styles.costInput}
                            />
                          </div>
                          <Button
                            icon="pi pi-trash"
                            severity="danger"
                            text
                            size="small"
                            onClick={() => handleRemoveEntry(item.productKey, idx)}
                            className={styles.removeBtn}
                            aria-label="Remove entry"
                          />
                        </div>
                      </div>
                    ))}
                    <Button
                      label="Add period"
                      icon="pi pi-plus"
                      text
                      size="small"
                      onClick={() => handleAddEntry(item.productKey)}
                      className={styles.addBtn}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {validationError && <Message severity="error" text={validationError} />}
      {saveError && <Message severity="error" text={saveError} />}

      {isDirty && (
        <div className={styles.railActions}>
          <Button label="Save" icon="pi pi-check" onClick={handleSave} loading={isSaving} size="small" />
          <Button label="Cancel" icon="pi pi-times" severity="secondary" outlined onClick={handleCancel} disabled={isSaving} size="small" />
        </div>
      )}
    </div>
  );
};

export default VoxRoiCostRail;
